/**
 * @file auth.interceptor.ts
 * @description Functional HTTP interceptor that adds the Authorization Bearer token
 * to all API requests. Excludes S3 presigned URLs and other external services
 * that use their own authentication.
 *
 * @see legacy/web-console-dev/src/auth.intercept.ts — original pattern
 */
import { HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { getScopedStorageItem } from '@kirigami/platform/browser';

/** Storage key for the access token (must match auth.service.ts) */
const TOKEN_KEY = 'atk';

/**
 * Adds `Authorization: Bearer {token}` to all outgoing HTTP requests,
 * except those to S3 presigned URLs or other external services.
 */
export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  // Skip auth header for external/S3 URLs (presigned URLs are self-authenticated)
  if (isExternalUrl(req.url)) {
    return next(req);
  }

  const token = getScopedStorageItem(TOKEN_KEY);
  if (token) {
    const cloned = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
    return next(cloned);
  }

  return next(req);
}

/**
 * Checks if a URL is external (S3, GCS, or non-Kirigami domain).
 * These URLs should NOT have the Bearer token attached.
 */
function isExternalUrl(url: string): boolean {
  return (
    url.includes('s3.amazonaws.com') ||
    url.includes('s3.eu-west-1.amazonaws.com') ||
    url.includes('storage.googleapis.com') ||
    (url.startsWith('https://') && !url.includes('kirigami.ai'))
  );
}
